import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { saveRecurringSchedule, getRecurringScheduleByAgreement } from '../utils/storageUtils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './RecurringShiftSetup.css';
import './RosterView.css';

const DAY_LABELS_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LABELS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function shortenItemName(name) {
  if (!name) return '';
  let s = name
    .replace(/^Assistance\s+(With|with)\s+(the\s+cost\s+of\s+the\s+)?/i, '')
    .replace(/^(the\s+preparation\s+and\s+delivery\s+of\s+)/i, '')
    .replace(/Activities?\s*-?\s*/i, '')
    .replace(/\s*-\s*Standard\s*/i, ' - ')
    .replace(/Weekday/i, 'Wkday')
    .replace(/Daytime/i, 'Day')
    .replace(/Evening/i, 'Eve')
    .replace(/Night-?Time/i, 'Night')
    .replace(/Sleepover/i, 'Sleep')
    .replace(/Public Holiday/i, 'Pub Hol')
    .replace(/Saturday/i, 'Sat')
    .replace(/Sunday/i, 'Sun')
    .replace(/Personal Domestic/i, 'Domestic')
    .replace(/\s*-\s*$/, '')
    .replace(/^\s*-\s*/, '')
    .trim();
  if (s.length > 22) s = s.substring(0, 20) + '…';
  return s;
}

function createEmptyDay() {
  return { duration: null, startTime: null, endTime: null, serviceItemId: null, serviceItemName: null, serviceItemNumber: null, unit: null };
}

function getMatchingItems(supportItems, dayKey) {
  if (!supportItems || supportItems.length === 0) return [];
  const isWeekday = ['mon', 'tue', 'wed', 'thu', 'fri'].includes(dayKey);
  const isSaturday = dayKey === 'sat';
  const isSunday = dayKey === 'sun';

  return supportItems.filter(item => {
    const name = (item.name || '').toLowerCase();
    const hasSaturday = name.includes('saturday');
    const hasSunday = name.includes('sunday');
    const hasEvening = name.includes('evening');
    const hasPublicHoliday = name.includes('public holiday');
    const hasDayKeyword = hasSaturday || hasSunday || hasEvening || hasPublicHoliday;

    if (hasPublicHoliday) return true;
    if (hasEvening) return true;
    if (hasSaturday) return isSaturday;
    if (hasSunday) return isSunday;
    if (!hasDayKeyword) return isWeekday;
    return false;
  });
}

// Determine required span from SA support item frequencies
function getRequiredDays(supportItems) {
  if (!supportItems || supportItems.length === 0) return 7;
  let maxDays = 7;
  supportItems.forEach(item => {
    const freq = (item.frequency || 'weekly').toLowerCase();
    if (freq === 'monthly') maxDays = Math.max(maxDays, 28);
    else if (freq === 'fortnightly') maxDays = Math.max(maxDays, 14);
  });
  return maxDays;
}

function toLocalDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateShort(date) {
  const d = date.getDate();
  const m = date.getMonth() + 1;
  return `${d}/${m}`;
}

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isToday(date) {
  const now = new Date();
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function RecurringShiftSetup({ agreement, onClose, onSave }) {
  const supportItems = agreement?.supportItems || [];
  const spanDays = getRequiredDays(supportItems);
  const spanLabel = spanDays === 7 ? 'Weekly' : spanDays === 14 ? 'Fortnightly' : 'Monthly';

  // Start from the Monday of the current week
  const [startDate, setStartDate] = useState(() => getMonday(new Date()));
  const [saving, setSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Build the array of dates for the span
  const dates = useMemo(() => {
    const result = [];
    for (let i = 0; i < spanDays; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      result.push(d);
    }
    return result;
  }, [startDate, spanDays]);

  // Schedule data keyed by date string (YYYY-MM-DD)
  const [schedule, setSchedule] = useState({});

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load existing schedule
  useEffect(() => {
    if (agreement?.id) {
      const existing = getRecurringScheduleByAgreement(agreement.id);
      if (existing?.blocks) {
        // Convert old block format (day-of-week based) to date-keyed format
        // We'll populate dates based on day-of-week mapping
        const dateSchedule = {};
        existing.blocks.forEach((block, blockIndex) => {
          const oldDayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
          oldDayKeys.forEach(dk => {
            const d = block.days?.[dk];
            if (d && d.duration) {
              // This shift applies to this day of week — we'll store it by day key for now
              if (!dateSchedule[`_daykey_${dk}_${blockIndex}`]) {
                dateSchedule[`_daykey_${dk}_${blockIndex}`] = d;
              }
            }
          });
        });
        // Also load date-keyed data if it exists (new format)
        if (existing.dateSchedule) {
          Object.assign(dateSchedule, existing.dateSchedule);
        }
        setSchedule(existing.dateSchedule || {});

        // If old format only, pre-fill dates from day-of-week pattern
        if (!existing.dateSchedule && existing.blocks) {
          const migrated = {};
          dates.forEach(date => {
            const dk = DAY_KEYS[date.getDay()];
            existing.blocks.forEach(block => {
              const d = block.days?.[dk];
              if (d && d.duration) {
                const key = toLocalDateStr(date);
                if (!migrated[key]) {
                  migrated[key] = [];
                }
                migrated[key].push({ ...d });
              }
            });
          });
          setSchedule(migrated);
        }
      }
    }
  }, [agreement, dates]);

  const autoFillServiceItem = useCallback((dateKey, shiftIndex, currentSchedule) => {
    const shifts = currentSchedule[dateKey];
    if (!shifts || !shifts[shiftIndex]) return currentSchedule;
    const shift = shifts[shiftIndex];
    if (!shift.duration || shift.serviceItemId) return currentSchedule;

    const date = new Date(dateKey + 'T00:00:00');
    const dayKey = DAY_KEYS[date.getDay()];
    const matching = getMatchingItems(supportItems, dayKey);
    if (matching.length === 1) {
      const item = matching[0];
      const newSchedule = { ...currentSchedule };
      newSchedule[dateKey] = [...shifts];
      newSchedule[dateKey][shiftIndex] = {
        ...shift,
        serviceItemId: item.itemNumber || item.id,
        serviceItemName: item.name,
        serviceItemNumber: item.itemNumber,
        unit: item.unit || 'hour'
      };
      return newSchedule;
    }
    return currentSchedule;
  }, [supportItems]);

  const handleDayChange = (dateKey, shiftIndex, field, value) => {
    setSchedule(prev => {
      let newSchedule = { ...prev };
      if (!newSchedule[dateKey]) {
        newSchedule[dateKey] = [createEmptyDay()];
      } else {
        newSchedule[dateKey] = [...newSchedule[dateKey]];
      }
      if (!newSchedule[dateKey][shiftIndex]) {
        newSchedule[dateKey][shiftIndex] = createEmptyDay();
      }
      newSchedule[dateKey][shiftIndex] = { ...newSchedule[dateKey][shiftIndex], [field]: value || null };

      if (field === 'duration' && value) {
        newSchedule = autoFillServiceItem(dateKey, shiftIndex, newSchedule);
      }
      return newSchedule;
    });
  };

  const handleServiceItemSelect = (dateKey, shiftIndex, itemNumber) => {
    const item = supportItems.find(i => (i.itemNumber || i.id) === itemNumber);
    if (!item) {
      handleDayChange(dateKey, shiftIndex, 'serviceItemId', null);
      return;
    }
    setSchedule(prev => {
      const newSchedule = { ...prev };
      newSchedule[dateKey] = [...(newSchedule[dateKey] || [createEmptyDay()])];
      newSchedule[dateKey][shiftIndex] = {
        ...newSchedule[dateKey][shiftIndex],
        serviceItemId: item.itemNumber || item.id,
        serviceItemName: item.name,
        serviceItemNumber: item.itemNumber,
        unit: item.unit || 'hour'
      };
      return newSchedule;
    });
  };

  const addShiftToDate = (dateKey) => {
    setSchedule(prev => {
      const newSchedule = { ...prev };
      newSchedule[dateKey] = [...(newSchedule[dateKey] || []), createEmptyDay()];
      return newSchedule;
    });
  };

  const removeShiftFromDate = (dateKey, shiftIndex) => {
    setSchedule(prev => {
      const newSchedule = { ...prev };
      const shifts = [...(newSchedule[dateKey] || [])];
      shifts.splice(shiftIndex, 1);
      newSchedule[dateKey] = shifts.length > 0 ? shifts : undefined;
      if (!newSchedule[dateKey]) delete newSchedule[dateKey];
      return newSchedule;
    });
  };

  const prevPeriod = () => {
    setStartDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() - spanDays);
      return d;
    });
  };

  const nextPeriod = () => {
    setStartDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + spanDays);
      return d;
    });
  };

  const goToThisWeek = () => {
    setStartDate(getMonday(new Date()));
  };

  // Also convert back to old block format for backward compat with RosterView
  const handleSave = async () => {
    setSaving(true);
    try {
      const existing = getRecurringScheduleByAgreement(agreement.id);

      // Convert date schedule to day-of-week blocks for backward compat
      const dayOfWeekShifts = {};
      Object.entries(schedule).forEach(([dateKey, shifts]) => {
        if (!shifts) return;
        const date = new Date(dateKey + 'T00:00:00');
        const dk = DAY_KEYS[date.getDay()];
        // Use the first shift for this day-of-week (simple mapping)
        if (!dayOfWeekShifts[dk] && shifts[0]?.duration) {
          dayOfWeekShifts[dk] = shifts[0];
        }
      });

      // Build a single block from day-of-week data
      const oldDayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
      const blockDays = {};
      oldDayKeys.forEach(dk => {
        blockDays[dk] = dayOfWeekShifts[dk] || createEmptyDay();
      });

      const scheduleData = {
        ...(existing || {}),
        serviceAgreementId: agreement.id,
        clientId: agreement.participant?.id || null,
        updatedAt: new Date().toISOString(),
        blocks: [{ id: 'main', days: blockDays }],
        dateSchedule: schedule,
        spanDays,
      };
      if (!existing) {
        scheduleData.createdAt = new Date().toISOString();
      }
      saveRecurringSchedule(scheduleData);
      if (onSave) onSave(scheduleData);
      onClose();
    } catch (error) {
      console.error('Error saving recurring schedule:', error);
    } finally {
      setSaving(false);
    }
  };

  // Group dates by week for display
  const weeks = useMemo(() => {
    const result = [];
    for (let i = 0; i < dates.length; i += 7) {
      result.push(dates.slice(i, i + 7));
    }
    return result;
  }, [dates]);

  // Compute period label
  const periodLabel = useMemo(() => {
    const first = dates[0];
    const last = dates[dates.length - 1];
    if (first.getMonth() === last.getMonth()) {
      return `${first.getDate()} – ${last.getDate()} ${MONTH_NAMES[first.getMonth()]} ${first.getFullYear()}`;
    }
    return `${first.getDate()} ${MONTH_NAMES[first.getMonth()]} – ${last.getDate()} ${MONTH_NAMES[last.getMonth()]} ${last.getFullYear()}`;
  }, [dates]);

  return (
    <div className="recurring-shift-overlay" onClick={onClose}>
      <div className="recurring-shift-modal" onClick={e => e.stopPropagation()}>
        <button className="btn-close" onClick={onClose} style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 10 }}>×</button>
        
        <h2 style={{ marginBottom: '0.25rem', paddingRight: '2rem' }}>Set Up {spanLabel} Recurring Shifts</h2>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
          {agreement.participant?.name} — {agreement.agreementNumber || `SA-${agreement.id?.substring(0, 8)}`}
        </p>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem', fontSize: '0.8rem', fontStyle: 'italic' }}>
          {spanDays}-day view based on your support item frequencies
        </p>

        {/* Period navigation */}
        <div className="roster-month-nav" style={{ marginBottom: '1rem' }}>
          <button className="roster-nav-btn" onClick={prevPeriod} aria-label="Previous period">
            <ChevronLeft size={20} />
          </button>
          <div className="roster-month-label">
            <span className="roster-month-name">{periodLabel}</span>
          </div>
          <button className="roster-nav-btn" onClick={nextPeriod} aria-label="Next period">
            <ChevronRight size={20} />
          </button>
          <button className="roster-today-btn" onClick={goToThisWeek}>This Week</button>
        </div>

        <div className="recurring-blocks-container">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="recurring-block">
              {spanDays > 7 && (
                <div className="recurring-block-header">
                  <span className="recurring-block-label">Week {weekIndex + 1}</span>
                </div>
              )}

              <div className="recurring-grid-wrapper">
                <div className="recurring-grid">
                  {/* Header row with day name + date */}
                  <div className="recurring-grid-cell recurring-grid-label"></div>
                  {week.map((date, i) => (
                    <div key={i} className={`recurring-grid-cell recurring-grid-day-header${isToday(date) ? ' today' : ''}`}>
                      <span className="day-full">{DAY_LABELS_FULL[date.getDay()]}</span>
                      <span className="day-short">{DAY_LABELS_SHORT[date.getDay()]}</span>
                      <span className="day-date">{formatDateShort(date)}</span>
                    </div>
                  ))}

                  {/* Duration row */}
                  <div className="recurring-grid-cell recurring-grid-label">Duration</div>
                  {week.map((date) => {
                    const dateKey = toLocalDateStr(date);
                    const shifts = schedule[dateKey] || [];
                    const shift = shifts[0] || {};
                    return (
                      <div key={dateKey} className={`recurring-grid-cell${isToday(date) ? ' today-col' : ''}`}>
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          placeholder="hrs"
                          value={shift.duration || ''}
                          onChange={e => {
                            if (!schedule[dateKey]?.length) {
                              setSchedule(prev => ({ ...prev, [dateKey]: [createEmptyDay()] }));
                            }
                            handleDayChange(dateKey, 0, 'duration', e.target.value ? parseFloat(e.target.value) : null);
                          }}
                          className="recurring-input"
                        />
                      </div>
                    );
                  })}

                  {/* Start Time row */}
                  <div className="recurring-grid-cell recurring-grid-label">Start</div>
                  {week.map((date) => {
                    const dateKey = toLocalDateStr(date);
                    const shifts = schedule[dateKey] || [];
                    const shift = shifts[0] || {};
                    return (
                      <div key={dateKey} className={`recurring-grid-cell${isToday(date) ? ' today-col' : ''}`}>
                        <input
                          type="time"
                          value={shift.startTime || ''}
                          onChange={e => handleDayChange(dateKey, 0, 'startTime', e.target.value)}
                          className="recurring-input"
                        />
                      </div>
                    );
                  })}

                  {/* End Time row */}
                  <div className="recurring-grid-cell recurring-grid-label">End</div>
                  {week.map((date) => {
                    const dateKey = toLocalDateStr(date);
                    const shifts = schedule[dateKey] || [];
                    const shift = shifts[0] || {};
                    return (
                      <div key={dateKey} className={`recurring-grid-cell${isToday(date) ? ' today-col' : ''}`}>
                        <input
                          type="time"
                          value={shift.endTime || ''}
                          onChange={e => handleDayChange(dateKey, 0, 'endTime', e.target.value)}
                          className="recurring-input"
                        />
                      </div>
                    );
                  })}

                  {/* Service Item row */}
                  <div className="recurring-grid-cell recurring-grid-label">Item</div>
                  {week.map((date) => {
                    const dateKey = toLocalDateStr(date);
                    const dayKey = DAY_KEYS[date.getDay()];
                    const shifts = schedule[dateKey] || [];
                    const shift = shifts[0] || {};
                    const matching = getMatchingItems(supportItems, dayKey);
                    return (
                      <div key={dateKey} className={`recurring-grid-cell${isToday(date) ? ' today-col' : ''}`}>
                        <select
                          value={shift.serviceItemId || ''}
                          onChange={e => handleServiceItemSelect(dateKey, 0, e.target.value)}
                          className="recurring-input recurring-select"
                          title={shift.serviceItemName || ''}
                        >
                          <option value="">—</option>
                          {matching.map(item => (
                            <option key={item.itemNumber || item.id} value={item.itemNumber || item.id} title={item.name}>
                              {isMobile ? shortenItemName(item.name) : `${item.name} (${item.itemNumber})`}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="recurring-actions">
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Schedule'}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Skip</button>
        </div>
      </div>
    </div>
  );
}
